import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getReports, getReport, generateReport, deleteReport } from "@/api/reports";
import { getCompanies } from "@/api/companies";
import { useAuthStore } from "@/stores/auth-store";
import { renderMarkdown } from "@/lib/markdown";
import { formatCnpj } from "@/lib/utils";
import { FileText, Plus, Loader2, Download, Trash2, Building2, ChevronDown } from "lucide-react";

export function ReportsPage() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.token);
  const apiBase = import.meta.env.VITE_API_URL || "";

  const { data: listData, isLoading } = useQuery({
    queryKey: ["reports"],
    queryFn: () => getReports(),
  });

  const { data: detailData } = useQuery({
    queryKey: ["report", selectedId],
    queryFn: () => getReport(selectedId!),
    enabled: selectedId !== null,
  });

  const { data: companiesData } = useQuery({
    queryKey: ["companies-for-reports"],
    queryFn: () => getCompanies({ limit: 100 }),
  });

  const generate = useMutation({
    mutationFn: (params: { companyId?: number; type: string }) => generateReport(params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports"] });
      setShowGenerate(false);
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteReport(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports"] });
      if (selectedId === deleteId) setSelectedId(null);
      setDeleteId(null);
    },
  });

  const reports = listData?.reports || [];
  const companies = companiesData?.companies || [];
  const detail = detailData?.report;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Fiscal analysis reports</p>
        </div>
        <button
          onClick={() => setShowGenerate(true)}
          disabled={generate.isPending}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50"
        >
          {generate.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Generate Report
        </button>
      </div>

      {/* Generate dialog */}
      {showGenerate && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-medium text-slate-900 mb-3">Choose report type</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => generate.mutate({ type: "portfolio_summary" })}
              disabled={generate.isPending}
              className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 text-left disabled:opacity-50"
            >
              <Building2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900">Portfolio Summary</p>
                <p className="text-xs text-slate-500">Overview of all monitored companies</p>
              </div>
            </button>
            {companies.map((c) => (
              <button
                key={c.id}
                onClick={() => generate.mutate({ companyId: c.id, type: "company_analysis" })}
                disabled={generate.isPending}
                className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 text-left disabled:opacity-50"
              >
                <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-900 truncate">{c.razao_social || formatCnpj(c.cnpj)}</p>
                  <p className="text-xs text-slate-500">Company analysis</p>
                </div>
              </button>
            ))}
          </div>
          <button onClick={() => setShowGenerate(false)} className="mt-3 text-xs text-slate-500 hover:text-slate-700">
            Cancel
          </button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Report list */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-medium text-slate-900">All Reports</h2>
          </div>
          {isLoading ? (
            <div className="px-4 py-8 text-center text-slate-400">Loading...</div>
          ) : reports.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No reports yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {reports.map((r: Record<string, unknown>) => (
                <div
                  key={r.id as number}
                  className={`group flex items-center justify-between px-4 py-3 hover:bg-slate-50 cursor-pointer ${selectedId === r.id ? "bg-slate-50" : ""}`}
                >
                  <button
                    onClick={() => setSelectedId(r.id as number)}
                    className="flex-1 text-left min-w-0"
                  >
                    <p className="text-sm font-medium text-slate-900 truncate">{String(r.title)}</p>
                    <p className="text-xs text-slate-400">{new Date(String(r.created_at)).toLocaleDateString("pt-BR")}</p>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteId(r.id as number); }}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0 ml-2"
                    title="Delete report"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Report viewer */}
        <div className="col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          {detail ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">{String(detail.title)}</h2>
                <a
                  href={`${apiBase}/reports/${selectedId}/pdf?token=${token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
                >
                  <Download className="w-3.5 h-3.5" /> Save as PDF
                </a>
              </div>
              <div
                className="max-w-none"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(String(detail.content_markdown || "")) }}
              />
              <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-400">
                {new Date(String(detail.created_at)).toLocaleString("pt-BR")}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-sm text-slate-400">
              Select a report to view
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDeleteId(null)}>
          <div className="bg-white rounded-xl border border-slate-200 p-6 w-full max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-900 mb-2">Delete report</h3>
            <p className="text-sm text-slate-500 mb-5">
              This will permanently delete this report and its PDF. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => remove.mutate(deleteId)}
                disabled={remove.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {remove.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
